import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowUpRight, Bot, CheckCircle, Loader2, MessageCircle, Mic, Phone, Send, Sparkles, Square, Target,
  Volume2, VolumeX, Waypoints, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RouterPill = ({ label, value, testId }) => (
  <div className="rounded-full border border-primary/20 bg-primary/10 px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-primary" data-testid={testId}>
    {label} · {value}
  </div>
);

const CallOfferCard = () => {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const requestCall = async (event) => {
    event.preventDefault();
    setSending(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/bland/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phone.trim(), visitor_name: name.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : 'Escribe el teléfono en formato internacional, ej. +34 600 111 222';
        throw new Error(detail);
      }
      setStatus({ ok: true, text: '¡Listo! Aynara te llamará en menos de un minuto. 📞' });
    } catch (error) {
      setStatus({ ok: false, text: error.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-[88%] rounded-2xl overflow-hidden border border-primary/30 rounded-bl-sm bg-slate-900/90" data-testid="chatbot-call-offer-card">
      <div className="bg-gradient-to-r from-primary/20 to-secondary/20 px-4 py-2 flex items-center gap-2">
        <Phone className="w-4 h-4 text-primary" />
        <span className="text-primary font-semibold text-xs">¿Te llamo por teléfono?</span>
      </div>
      {status?.ok ? (
        <p className="px-4 py-3 text-sm text-text-body" data-testid="chatbot-call-offer-success">{status.text}</p>
      ) : (
        <form onSubmit={requestCall} className="px-4 py-3 space-y-2">
          <p className="text-xs text-text-muted">Veo que te interesa. Puedo llamarte ahora mismo y resolverlo de viva voz en 2 minutos.</p>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+34 600 111 222"
            required
            className="w-full rounded-lg border border-border-subtle bg-slate-950/80 px-3 py-2 text-sm text-text-heading placeholder:text-text-muted focus:border-primary outline-none"
            data-testid="chatbot-call-offer-phone"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre (opcional)"
            className="w-full rounded-lg border border-border-subtle bg-slate-950/80 px-3 py-2 text-sm text-text-heading placeholder:text-text-muted focus:border-primary outline-none"
            data-testid="chatbot-call-offer-name"
          />
          {status && !status.ok && <p className="text-xs text-red-400" data-testid="chatbot-call-offer-error">{status.text}</p>}
          <button type="submit" disabled={sending || !phone.trim()} className="w-full rounded-lg bg-primary py-2 text-sm font-bold text-slate-950 disabled:opacity-40 hover:brightness-110 transition" data-testid="chatbot-call-offer-submit">
            {sending ? 'Llamando…' : 'Sí, llámame ahora'}
          </button>
        </form>
      )}
    </div>
  );
};

const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [goal, setGoal] = useState('activar_neuralia_os');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(null);
  const [suggestedAction, setSuggestedAction] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [voiceReply, setVoiceReply] = useState(false);
  const voiceReplyRef = useRef(false);
  const replyAudioRef = useRef(null);
  const [aynaraVoice, setAynaraVoice] = useState('nova');
  const aynaraVoiceRef = useRef('nova');

  const speakText = async (text) => {
    try {
      const res = await fetch(`${API}/chat/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: String(text || '').slice(0, 1500), voice: aynaraVoiceRef.current }),
      });
      if (!res.ok) throw new Error('tts');
      const blob = await res.blob();
      if (replyAudioRef.current) replyAudioRef.current.pause();
      const audio = new Audio(URL.createObjectURL(blob));
      replyAudioRef.current = audio;
      await audio.play();
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', text: '🔇 Ahora mismo no puedo reproducir mi voz, pero puedes seguir leyéndome aquí. Vuelve a activar el altavoz en unos segundos.' }]);
    }
  };

  const toggleVoiceReply = () => {
    const next = !voiceReplyRef.current;
    voiceReplyRef.current = next;
    setVoiceReply(next);
    if (!next && replyAudioRef.current) replyAudioRef.current.pause();
  };

  useEffect(() => {
    if (open && !hasGreeted) {
      setMessages([{
        role: 'assistant',
        text: 'Hola, soy Aynara. Ya no soy solo un chat: ahora puedo orientarte con routing visible, memoria por objetivo y una acción sugerida en vivo para ayudarte a activar Neuralia369 OS con claridad.',
      }]);
      setHasGreeted(true);
    }
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open, hasGreeted]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentRoute, suggestedAction]);

  const submitText = async (rawText, speak = false) => {
    const userMessage = (rawText || '').trim();
    if (!userMessage || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: userMessage }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          goal,
          mode: 'premium_public_chat',
        }),
      });
      const data = await res.json();

      if (res.ok) {
        setSessionId(data.session_id);
        setCurrentRoute(data.aynara_router || null);
        setSuggestedAction(data.suggested_next_action || null);
        if (data.goal) setGoal(data.goal);
        setMessages((prev) => [...prev, { role: 'assistant', text: data.response }]);
        if ((speak || voiceReplyRef.current) && data.response) speakText(data.response);
        if (data.booking) setMessages((prev) => [...prev, { role: 'booking', booking: data.booking }]);
        if (data.offer_call) setMessages((prev) => [...prev, { role: 'call_offer' }]);
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', text: 'Ups, algo salió mal. Inténtalo de nuevo en unos segundos.' }]);
      }
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', text: 'Parece que hay un problema de conexión. Inténtalo de nuevo en unos segundos.' }]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    await submitText(input);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : undefined;
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (event) => { if (event.data.size > 0) audioChunksRef.current.push(event.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size < 1000) return;
        setTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('audio', blob, 'voz-aynara.webm');
          const res = await fetch(`${API}/chat/voice`, { method: 'POST', body: formData });
          const data = await res.json();
          if (!res.ok || !data.text) throw new Error(data.detail || 'No se entendió el audio');
          await submitText(data.text, true);
        } catch (error) {
          setMessages((prev) => [...prev, { role: 'assistant', text: 'No pude entender el audio. Prueba a hablar más cerca del micrófono o escribe tu mensaje.' }]);
        } finally {
          setTranscribing(false);
        }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', text: 'Necesito permiso del micrófono para escucharte. Actívalo en tu navegador y vuelve a intentarlo.' }]);
    }
  };

  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  const quickActions = [
    { label: 'Activar Neuralia OS', goal: 'activar_neuralia_os' },
    { label: 'Servicios y soluciones', goal: 'servicios_creditos' },
    { label: 'Conectar herramientas', goal: 'conectar_herramientas' },
    { label: 'Demo estratégica', goal: 'demo_estrategica' },
  ];

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-24 right-6 z-[9999] w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg shadow-primary/20"
            data-testid="chatbot-toggle"
            aria-label="Abrir chat"
          >
            <MessageCircle className="w-6 h-6 text-background" />
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-secondary animate-pulse" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 right-6 z-[9999] w-[410px] max-w-[calc(100vw-1rem)] h-[680px] max-h-[calc(100vh-3rem)] flex flex-col rounded-[24px] overflow-hidden border border-border-subtle shadow-2xl shadow-primary/10 bg-slate-950/92 backdrop-blur-2xl"
            data-testid="chatbot-window"
          >
            <div className="px-5 py-4 flex items-center justify-between border-b border-border-subtle bg-slate-950/70">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-slate-900/70 border border-primary/20 flex items-center justify-center">
                  <div className="aynara-listening-core scale-[0.42]" data-testid="aynara-listening-core" />
                </div>
                <div>
                  <h3 className="text-text-heading font-semibold text-sm">Aynara</h3>
                  <span className="text-primary text-xs flex items-center gap-1" data-testid="chatbot-role-label">
                    <span className="w-2 h-2 rounded-full bg-primary inline-block animate-pulse" />
                    Núcleo conversacional Neuralia
                  </span>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-text-muted hover:text-text-heading transition-colors p-1" data-testid="chatbot-close" aria-label="Cerrar chat">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-4 py-3 border-b border-border-subtle bg-slate-950/75" data-testid="chatbot-command-header">
              <div className="flex items-center gap-2 text-text-heading mb-3">
                <Waypoints className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold">Routing visible + memoria por objetivo</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="chatbot-goal-chip-row">
                {quickActions.map((item) => (
                  <button
                    key={item.goal}
                    onClick={() => setGoal(item.goal)}
                    className={`rounded-full px-3 py-2 text-[10px] uppercase tracking-[0.16em] border ${goal === item.goal ? 'border-primary/25 bg-primary/10 text-primary' : 'border-border-subtle text-text-muted'}`}
                    data-testid={`chatbot-goal-chip-${item.goal}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {currentRoute && (
                <div className="rounded-[20px] border border-primary/15 bg-primary/5 p-3" data-testid="chatbot-live-routing-card">
                  <div className="flex items-center gap-2 mb-3 text-text-heading"><Bot className="w-4 h-4 text-primary" /><span className="text-sm font-semibold">Ruta actual</span></div>
                  <div className="flex flex-wrap gap-2">
                    <RouterPill label="Agente" value={currentRoute.primary_agent} testId="chatbot-router-primary-agent" />
                    <RouterPill label="Coste" value={currentRoute.cost_profile} testId="chatbot-router-cost-profile" />
                    <RouterPill label="Riesgo" value={currentRoute.risk_profile} testId="chatbot-router-risk-profile" />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="chatbot-router-stack-list">
                    {(currentRoute.recommended_stack || []).map((item) => (
                      <span key={item} className="rounded-full border border-primary/20 bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-950/92" data-testid="chatbot-messages">
              {messages.map((msg, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'call_offer' ? (
                    <CallOfferCard />
                  ) : msg.role === 'booking' ? (
                    <div className="max-w-[85%] rounded-2xl overflow-hidden border border-primary/30 rounded-bl-sm bg-slate-900/90" data-testid="chatbot-booking-card">
                      <div className="bg-gradient-to-r from-primary/20 to-secondary/20 px-4 py-2 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /><span className="text-primary font-semibold text-xs">Cita confirmada</span></div>
                      <div className="px-4 py-3 text-sm space-y-1">
                        <p className="text-text-heading font-medium">{msg.booking.tipo_nombre}</p>
                        <p className="text-primary font-bold">{msg.booking.fecha} a las {msg.booking.hora}</p>
                        <p className="text-text-muted text-xs">{msg.booking.duracion} min · {msg.booking.email}</p>
                      </div>
                    </div>
                  ) : (
                    <div className={`max-w-[88%] ${msg.role === 'user' ? 'rounded-2xl px-4 py-3 text-sm leading-relaxed bg-primary text-slate-950 rounded-br-sm' : ''}`}>
                      {msg.role === 'assistant' && (
                        <div className="rounded-2xl px-4 py-3 text-sm leading-relaxed bg-slate-900/90 text-text-body border border-border-subtle rounded-bl-sm" data-testid="chatbot-assistant-message">
                          {msg.text.split('\n').map((line, j) => <React.Fragment key={j}>{line}{j < msg.text.split('\n').length - 1 && <br />}</React.Fragment>)}
                        </div>
                      )}
                      {msg.role === 'user' && msg.text.split('\n').map((line, j) => <React.Fragment key={j}>{line}{j < msg.text.split('\n').length - 1 && <br />}</React.Fragment>)}
                    </div>
                  )}
                </motion.div>
              ))}

              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                  <div className="bg-slate-900/90 border border-border-subtle rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}

              {suggestedAction && (
                <div className="rounded-[22px] border border-primary/20 bg-primary/10 p-4" data-testid="chatbot-suggested-action-card">
                  <div className="flex items-center gap-2 text-text-heading"><Target className="w-4 h-4 text-primary" /><span className="text-sm font-semibold">Acción sugerida en vivo</span></div>
                  <p className="mt-3 text-sm font-semibold text-text-heading">{suggestedAction.label}</p>
                  <p className="mt-2 text-sm text-text-body">{suggestedAction.description}</p>
                  <div className="mt-3 flex items-center gap-2 text-xs text-primary"><ArrowUpRight className="w-4 h-4" />Objetivo activo: {suggestedAction.focus}</div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {!loading && messages.length <= 1 && (
              <div className="px-4 pb-2 bg-slate-950/90 flex flex-wrap gap-2">
                {quickActions.map((action, i) => (
                  <button
                    key={i}
                    onClick={() => { setGoal(action.goal); setInput(action.label); }}
                    className="text-xs px-3 py-1.5 rounded-full bg-slate-900/90 border border-border-subtle text-text-body hover:border-primary hover:text-primary transition-colors"
                    data-testid={`quick-action-${i}`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={sendMessage} className="p-3 bg-slate-950/92 border-t border-border-subtle flex items-end gap-2 flex-shrink-0" data-testid="chatbot-form">
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-[10px] uppercase tracking-[0.16em] text-text-muted whitespace-nowrap" htmlFor="chatbot-input">Mensaje para Aynara</label>
                  {voiceReply && (
                    <select
                      value={aynaraVoice}
                      onChange={(event) => { setAynaraVoice(event.target.value); aynaraVoiceRef.current = event.target.value; }}
                      className="h-6 rounded-lg border border-border-subtle bg-slate-900/90 px-1.5 text-[10px] text-text-body focus:border-primary"
                      data-testid="chatbot-voice-selector"
                      title="Voz de Aynara"
                    >
                      <option value="nova">Voz: Cálida</option>
                      <option value="shimmer">Voz: Enérgica</option>
                      <option value="onyx">Voz: Profesional</option>
                    </select>
                  )}
                </div>
                <input
                  id="chatbot-input"
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Escribe tu mensaje..."
                  className="flex-1 bg-slate-900/90 border border-border-subtle rounded-xl px-4 py-3 text-sm text-text-heading placeholder:text-text-muted focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-colors w-full"
                  disabled={loading}
                  data-testid="chatbot-input"
                />
              </div>
              <button
                type="button"
                onClick={toggleVoiceReply}
                className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${voiceReply ? 'bg-primary text-white' : 'bg-slate-900/90 border border-border-subtle text-text-body hover:border-primary hover:text-primary'}`}
                data-testid="chatbot-voice-reply-toggle"
                aria-label={voiceReply ? 'Desactivar voz de Aynara' : 'Activar voz de Aynara'}
                title={voiceReply ? 'Aynara responde con voz (activado)' : 'Que Aynara responda con voz'}
              >
                {voiceReply ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={toggleRecording}
                disabled={loading || transcribing}
                className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${recording ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-900/90 border border-border-subtle text-text-body hover:border-primary hover:text-primary'} disabled:opacity-40`}
                data-testid="chatbot-mic"
                aria-label={recording ? 'Detener grabación' : 'Hablar con Aynara'}
                title={recording ? 'Detener y enviar' : 'Hablar con Aynara'}
              >
                {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <button type="submit" disabled={loading || !input.trim()} className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center text-slate-950 disabled:opacity-40 hover:scale-105 transition-transform flex-shrink-0" data-testid="chatbot-send" aria-label="Enviar mensaje">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;